import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/button/button';
import { RegisterButton } from '../components/button/register-button';
import { ImageCard } from '../components/card/image-card';
import { Container } from '../components/container/container';
import { Copy } from '../components/copy/copy';
import { Hero } from '../components/hero/hero';
import { HeroAction } from '../components/hero/hero-action';
import { Image } from '../components/image/image';
import { ImageLayout } from '../components/image/image-layout';
import { VideoLarge } from '../components/image/video-large';
import { LazyShow } from '../components/reveal/reveal';
import { ActiveAuctionQuery, useMeQuery } from '../graphql/types';
import { config } from '../utils/config';
import dayjs, { PDTTimeZone } from '../utils/dayjs';
import useActiveYear from '../hooks/useActiveYear';

export function Index() {
  const { isAuthenticated } = useAuth0();

  const { data, loading } = useMeQuery();

  const ACTIVE_YEAR = useActiveYear();

  const activeAuction = data?.activeAuction;

  const parseAuctionDate = (
    activeAuction: ActiveAuctionQuery['activeAuction']
  ) => {
    if (!activeAuction) return '';

    const startsTime = dayjs(activeAuction.startsAt)
      .tz(PDTTimeZone)
      .format('DD MMM YYYY h:mm A');
    const endsAtTime = dayjs(activeAuction.endsAt)
      .tz(PDTTimeZone)
      .format('DD MMM YYYY h:mm A z');

    return [`${startsTime} - ${endsAtTime}`, 'NAPA'].join(' • ');
  };

  if (loading) return <></>;

  return (
    <>
      <Hero
        title={activeAuction?.title ?? ''}
        copy={parseAuctionDate(activeAuction)}
        imageUrl={`${config.s3BucketDomain}${activeAuction?.heroImage ?? ''}`}
        mobileImageUrl={`${config.s3BucketDomain}${
          activeAuction?.heroImageMobile ?? ''
        }`}
        mobileImageUrl2x={`${config.s3BucketDomain}${
          activeAuction?.heroImageMobile2x ?? ''
        }`}
      />
      <HeroAction
        actionText="Auction overview"
        actions={
          <>
            <RegisterButton />
            <Button as={Link} to={`/${ACTIVE_YEAR}/lots`} variant="secondary">
              {isAuthenticated ? 'Go to auction' : 'Preview auction'}
            </Button>
          </>
        }
      />
      <Container>
        <Copy speed={-2}>
          <div
            dangerouslySetInnerHTML={{
              __html: activeAuction?.description ?? '',
            }}
          />
          <Button
            variant="secondary"
            as="a"
            style={{ textDecoration: 'none' }}
            href={activeAuction?.faqLink}
          >
            Frequently asked questions
          </Button>
          {/* TODO(sebastienne): Update with correct listing once link is available */}
          {/* <br />
          <br />
          <Button
            variant="secondary"
            as="a"
            style={{ textDecoration: 'none' }}
            href="https://www.sothebyswine.com/custom/assets/Files/Barrel%20Auction%202023%20-%20Lot%20Listing%2005_23_23.xlsx"
          >
            Excel lot listing
          </Button> */}
        </Copy>
        <ImageLayout>
          <LazyShow>
            <ImageCard
              speed={-1}
              aspect={864 / 611}
              image={
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet="/images/napa1_mobile.jpg,
                          /images/napa1_mobile@2x.jpg 2x"
                  />
                  <Image
                    srcSet="/images/napa1.jpg,
                          /images/napa1@2x.jpg 2x"
                    src="/images/napa1@2x.jpg"
                    alt="Napa Valley"
                    fit="cover"
                  />
                </picture>
              }
              title="Napa Valley"
              copy="We are known for producing outstanding wines. Our great diversity in soil types, microclimates and topography has led our vintners and growers to create defined grape-growing areas within Napa Valley, to help distinguish the effects of this diversity on the wine. These areas are called American Viticultural Areas, or AVAs.
The Napa Valley is itself an AVA, and it has been since it received its own designation in 1981. It is California’s first recognized AVA and the second in the United States."
            />
          </LazyShow>
          <LazyShow>
            <ImageCard
              speed={-4}
              aspect={330 / 638}
              image={
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet="/images/napa2_mobile.jpg,
                          /images/napa2_mobile@2x.jpg 2x"
                  />
                  <Image
                    srcSet="/images/napa2.jpg,
                          /images/napa2@2x.jpg 2x"
                    src="/images/napa2.jpg"
                    alt="The soils of Napa Valley"
                    fit="cover"
                  />
                </picture>
              }
              title="The soils of Napa Valley"
              copy="Dramatic geological events and the steady march of time have combined to form the agricultural eden that is the Napa Valley of today.          "
            />
          </LazyShow>
        </ImageLayout>
        <Copy speed={-1}>
          <p>
            <strong>About Collective Napa Valley</strong>
          </p>
          <p>
            Collective Napa Valley is a year-round philanthropy program that
            brings people together to enjoy Napa Valley wines while raising
            funds for the good of its community. Throughout the year, members
            will engage with vintners and explore the region through a variety
            of programs and events online and in person.
          </p>
          <p>
            For more information, visit{' '}
            <a
              target="_blank"
              title="Collective Napa Valley"
              href="https://www.collectivenapavalley.org/"
              rel="noreferrer"
            >
              collectivenapavalley.org
            </a>
            .
          </p>
          <p>
            <br />
          </p>
          <p>
            <strong>Collective Napa Valley Barrel Auction</strong>
          </p>
          <p>
            The Collective Napa Valley Barrel Auction provides bidders with the
            opportunity to acquire Napa Valley’s finest wines currently aging in
            the barrel. Each winery donates a half-barrel of yet-to-be released
            wine for a total of 10 cases of each wine available at the auction.
            Ten individual bidders will take home a 12-bottle case of each of
            these Napa Valley wines.
          </p>
        </Copy>
      </Container>

      <VideoLarge youTubeId={activeAuction?.youtubeId ?? ''} />
    </>
  );
}
